import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  dataTransferService,
  DataTransferEntity,
  DataTransferImportResponse,
} from '../../services/dataTransferService';
import PageHeader from '../shell/PageHeader';
import { Field, FormSection, FormShell, Notice } from '../patterns';
import ImportResults from './ImportResults';

function downloadCsv(text: string, filename: string) {
  const blob = new Blob([text], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

const DataTransferPanel: React.FC<{ module: 'spraying' | 'fertilizing' }> = ({ module }) => {
  const { t } = useTranslation();
  const [entities, setEntities] = useState<DataTransferEntity[]>([]);
  const [selected, setSelected] = useState<string>('');
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<DataTransferImportResponse | null>(null);
  const [error, setError] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    dataTransferService
      .listEntities()
      .then(all => {
        const mine = all.filter(e => e.module === module);
        setEntities(mine);
        if (mine[0]) setSelected(mine[0].key);
      })
      .catch(() => setError(t('dataTransfer.error')));
  }, [module, t]);

  const today = () => new Date().toISOString().split('T')[0];

  const onTemplate = async () => {
    setError('');
    try {
      downloadCsv(
        await dataTransferService.downloadTemplate(selected),
        `${selected}_template.csv`,
      );
    } catch (err: any) {
      setError(err?.response?.data?.error ?? err?.message ?? t('dataTransfer.error'));
    }
  };

  const onExport = async () => {
    setError('');
    try {
      downloadCsv(
        await dataTransferService.exportData(selected),
        `${selected}_${today()}.csv`,
      );
    } catch (err: any) {
      setError(err?.response?.data?.error ?? err?.message ?? t('dataTransfer.error'));
    }
  };

  const onImport = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) return;
    setBusy(true);
    setError('');
    setResult(null);
    try {
      setResult(await dataTransferService.importData(selected, file));
      setFile(null);
      if (fileRef.current) fileRef.current.value = '';
    } catch (err: any) {
      setError(err?.response?.data?.error ?? err?.message ?? t('dataTransfer.error'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="data-transfer-panel">
      <PageHeader title={t('dataTransfer.title')} meta={t('dataTransfer.meta')} />

      {error && (
        <Notice tone="danger" className="mb-3">
          {error}
        </Notice>
      )}

      <FormShell
        sections={[
          { id: 'dt-entity', title: t('dataTransfer.selectEntity') },
          { id: 'dt-file', title: t('dataTransfer.selectFile') },
          { id: 'dt-results', title: t('dataTransfer.results') },
        ]}
        onSubmit={onImport}
        saveBar={null}
      >
        <FormSection id="dt-entity" title={t('dataTransfer.selectEntity')}>
          <div role="radiogroup" aria-label={t('dataTransfer.selectEntity')}>
            {entities.map(e => (
              <div className="form-check" key={e.key}>
                <input
                  className="form-check-input"
                  type="radio"
                  id={`dt-${e.key}`}
                  name="dtEntity"
                  value={e.key}
                  checked={selected === e.key}
                  onChange={() => setSelected(e.key)}
                />
                <label className="form-check-label" htmlFor={`dt-${e.key}`}>
                  {t(e.label)}
                </label>
              </div>
            ))}
          </div>
          <div className="mt-3 d-flex gap-2">
            <button
              type="button"
              className="pat-btn pat-btn--subtle"
              onClick={onTemplate}
            >
              {t('dataTransfer.downloadTemplate')}
            </button>
            <button
              type="button"
              className="pat-btn pat-btn--subtle"
              onClick={onExport}
            >
              {t('dataTransfer.exportData')}
            </button>
          </div>
        </FormSection>

        <FormSection id="dt-file" title={t('dataTransfer.selectFile')}>
          <Field label={t('dataTransfer.selectFile')} controlId="dt-csv">
            <input
              id="dt-csv"
              type="file"
              accept=".csv"
              ref={fileRef}
              className="pat-input"
              onChange={e => setFile((e.target as HTMLInputElement).files?.[0] ?? null)}
            />
          </Field>
          <div className="mt-3">
            <button
              type="submit"
              className="pat-btn pat-btn--primary"
              disabled={!file || busy}
            >
              {busy ? t('dataTransfer.importing') : t('dataTransfer.import')}
            </button>
          </div>
        </FormSection>

        <FormSection id="dt-results" title={t('dataTransfer.results')}>
          <ImportResults result={result} />
        </FormSection>
      </FormShell>
    </div>
  );
};

export default DataTransferPanel;
